import React, { useEffect, useMemo, useState } from "react";
import Carousel from "react-simply-carousel";
import Icon from "components/Elements/Icon/Icon.tsx";
import Image from "components/Elements/Image/Image.tsx";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { useUser } from "contexts/userContext/userContext.js";

const updateUserNotification = async ({
  clicked,
  displayed,
  templateId,
  notiId,
}) => {
  const body = {
    UserNotificationId: notiId,
    TemplateNotificationId: templateId,
    UserNotificationDisplayed: displayed,
    UserNotificationClicked: clicked,
    UserNotificationImportant: false,
    UserNotificationHidden: false,
  };

  const res = await apiRequest(
    "Notifications",
    !notiId
      ? "CreateUserNotificationSubscribersPromo"
      : "UpdateUserNotification",
    body,
    "POST"
  );

  if (res.status === "success") {
    return res.data.UserNotificationId;
  }
};

const SubscribersFeatureItem = ({
  title,
  text,
  image,
  imgAlt,
  isPremium,
  templateId,
  notiId,
}) => {
  const { getTextByKey, getHtmlByKey } = useContent();
  const { getUserData, isLoggedIn } = useUser();

  const altVal = getTextByKey(imgAlt);

  return (
    <div className="carousel-item">
      {isPremium ? (
        <div className="business-premium-title">
          <Image name="subscribers/business-icon" ext="png" />
          <div className="sub-users-business-premium-text">
            {getHtmlByKey("sub-users-premium-and-business")}
          </div>
        </div>
      ) : null}
      <div className="subscriber-features-promo-title">
        {getHtmlByKey(title)}
      </div>
      <div className="subscriber-features-promo-text">{getHtmlByKey(text)}</div>
      <div className="subscriber-features-promo-image">
        <Image name={image} ext="png" alt={altVal} title={altVal} />
      </div>
      <div
        className="subscriber-features-promo-button"
        onClick={() => {
          if (isLoggedIn()) {
            updateUserNotification({
              clicked: true,
              displayed: true,
              templateId: templateId,
              notiId,
            });
          }
        }}
      >
        <LinkToNewTab page="subscribers" isNewTab={true}>
          {getTextByKey("learn-more")}
        </LinkToNewTab>
      </div>
    </div>
  );
};

const SubscribersFeaturesPromo = ({
  setShowPromo,
  templateIdToShow,
  availableTemplatesIds,
}) => {
  const [notiId, setNotiId] = useState({});
  const { getLanguage } = useContent();
  const { getUserData, isLoggedIn } = useUser();

  const handleRequestChange = async (templateId) => {
    if (isLoggedIn()) {
      const res = await updateUserNotification({
        displayed: true,
        templateId: templateId,
      });
      if (res) {
        setNotiId((prev) => {
          return {
            ...prev,
            [templateId]: res,
          };
        });
      }
    }
  };

  const handleCarouselRequestChange = (event) => {
    const templateId = features[event].templateId;
    setActiveSlide(event);
    handleRequestChange(templateId);
  };

  const features = useMemo(() => {
    const allFeaturesArray = [
      {
        title: "subscribers-features-promo-title-storage",
        text: "subscribers-features-promo-text-storage",
        image: "home/1000-white",
        templateId: 41,
      },
      {
        title: "subscribers-features-promo-title-store",
        text: "subscribers-features-promo-text-store",
        image: "subscribers/extra-storage-subscribers",
        templateId: 42,
      },
      {
        title: "subscribers-features-promo-title-sub-users",
        text: "subscribers-features-promo-text-sub-users",
        image: "subscribers/sub-users-subscribers",
        isPremium: true,
        templateId: 43,
      },
      {
        title: "subscribers-features-promo-title-video",
        text: "subscribers-features-promo-text-video",
        image: "subscribers/mobile",
        templateId: 44,
      },
      {
        title: "subscribers-features-promo-title-social",
        text: "subscribers-features-promo-text-social",
        image: `subscribers/social_${getLanguage()}`,
        imgAlt: "image-description-social",
        templateId: 45,
      },
      {
        title: "subscribers-features-promo-title-security",
        text: "subscribers-features-promo-text-security",
        image: `subscribers/security_${getLanguage() === "he" ? "he" : "en"}`,
        imgAlt: "image-description-security",
        templateId: 46,
      },
      {
        title: "subscribers-features-promo-title-jumbopay",
        text: "subscribers-features-promo-text-jumbopay",
        image: "subscribers/jumbopay",
        templateId: 47,
      },
    ];

    const availableFeatures = allFeaturesArray.filter((feature) =>
      availableTemplatesIds.includes(feature.templateId)
    );

    return availableFeatures;
  }, [getLanguage(), availableTemplatesIds]);

  const indexToShow = features.findIndex(
    (feature) => feature.templateId === templateIdToShow
  );

  const [activeSlide, setActiveSlide] = useState(indexToShow);

  useEffect(() => {
    handleRequestChange(templateIdToShow);
  }, []);

  const width = document.querySelector(".home-upload-sidebar").offsetWidth;

  return (
    <div
      className="subscriber-features-promo"
      onKeyDown={(e) => {
        e.preventDefault();
      }}
    >
      <div
        className="close-button"
        onClick={() => {
          setShowPromo(false);
        }}
      >
        <Icon name="x" gray />
      </div>
      <Carousel
        containerProps={{
          className: "subscriber-features-promo-caurusel",
          style: { direction: "ltr" },
        }}
        innerProps={{
          style: { width, direction: "ltr" },
        }}
        preventScrollOnSwipe
        swipeTreshold={60}
        infinite={false}
        activeSlideIndex={activeSlide}
        onRequestChange={handleCarouselRequestChange}
        forwardBtnProps={{
          children: (
            <div className="arrow arrow-right">
              <Icon iconClass="arrow-carousel" name="arrow-right" />
            </div>
          ),
          className: "arrow-next",
        }}
        backwardBtnProps={{
          children: (
            <div className="arrow arrow-left">
              <Icon iconClass="arrow-carousel" name="arrow-left" />
            </div>
          ),
          className: "arrow-prev",
        }}
        dotsNav={{
          show: true,
          itemBtnProps: {
            className: "dot",
          },
          activeItemBtnProps: {
            className: "dot active",
          },
          containerProps: {
            className: "dots-container",
          },
        }}
        itemsToShow={1}
        speed={400}
      >
        {features.map((item, i) => (
          <SubscribersFeatureItem
            {...item}
            key={item.title}
            templateId={item.templateId}
            notiId={notiId[i]}
          />
        ))}
      </Carousel>
    </div>
  );
};

export default SubscribersFeaturesPromo;
